import JsonApi, { Params, Response } from 'devour-client';
import { plainToClass } from 'class-transformer';
import { camelCase, isArray, transform, isObject, snakeCase } from "lodash";
import { JsonApiPayload } from 'services/interfaces/JsonApiPayload';

type ClassConstructor<T = any> = {
  new (...args: any[]): T;
};

export class ServiceBase<T extends JsonApiPayload> {
  jsonApi: JsonApi;
  entityCtor: ClassConstructor<T>
  entityName: string;
  extraModels = new Map<string, ClassConstructor<T>>();

  constructor(jsonApi: JsonApi, entityName: string, schema: any, entityCtor?: ClassConstructor<T>) {
    jsonApi.define(entityName, schema);
    this.entityName = entityName;
    this.jsonApi = jsonApi;
    this.entityCtor = entityCtor;
  }

  define(entityName: string, entityCtor?: ClassConstructor) {
    const getSchema = entityCtor['getSchema'] as Function;
    if (!getSchema) {
      throw new Error(`Schema definition for ${entityName} not found`);
    }
    const schema = getSchema.apply(entityCtor);
    this.jsonApi.define(entityName, schema);
    this.extraModels.set(entityName, entityCtor);
  }

  async find(params?: Params) {
    const result = await this.jsonApi.findAll(this.entityName, params);
    const data = transformCollection<T>(result.data, this.entityCtor);
    return data;
  }
  
  async findById(id: string, params?: Params) {
    const result = await this.jsonApi.find(this.entityName, id, params);
    const data = transformItem<T>(result.data, this.entityCtor);
    return data;
  }

  async findByIds(ids: string[]) {
    const joinedIds = ids.join(',');
    const result = await this.jsonApi.find(this.entityName, joinedIds);
    const array = Array.isArray(result.data) ? result.data : [result.data];
    const data = transformCollection<T>(array, this.entityCtor);
    return data;
  }

  async getAll() {
    const filters = {
      page: { number: 1, size: 200 }
    };
    const result = await this.jsonApi.findAll(this.entityName, filters);
    const data = transformCollection<T>(result.data, this.entityCtor);
    return data;
  }

  async createOrUpdate(payload: T, params?: Params, meta?: any) {
    const rawPayload = snakize(payload);
    let result: Response;
    if (payload.id) {
      result = await this.jsonApi.update(this.entityName, rawPayload, params, meta);
    }
    else {
      result = await this.jsonApi.create(this.entityName, rawPayload, params, meta);
    }
    const data = transformItem<T>(result.data, this.entityCtor);
    return data;
  }

  async get<P>(entityName: string, params?: Params) {
    const ctor = this.extraModels.get(entityName);
    if (!ctor) throw new Error(`Model definition for '${entityName}' not found`);

    const result = await this.jsonApi.findAll(entityName, params);
    const data = transformPayload(result.data, ctor) as P;
    return data;
  }

  async post<P>(entityName: string, payload: Payload, params?: any, meta?: any) {
    const ctor = this.extraModels.get(entityName);
    if (!ctor) throw new Error(`Model definition for '${entityName}' not found`);

    const result = await tryCall(this.jsonApi.create(entityName, payload, params, meta));
    const data = transformPayload(result.data, ctor) as P;
    return data;
  }
}

type Payload = Record<string, unknown>

function transformItem<T>(data: any, entityCtor?: ClassConstructor) {
  const plainItem = camelize(data);
  const entity = plainToClass(entityCtor, plainItem);
  return entity as T;
}

function transformCollection<T>(data: any, entityCtor?: ClassConstructor) {
  if (Array.isArray(data)) {
    const plainCollection = data.map(item => camelize(item));
    const entityCollection = plainCollection.map(item => plainToClass(entityCtor, item));
    return entityCollection as T[];
  }
}

function transformPayload(data: any, entityCtor?: ClassConstructor) {
  if (Array.isArray(data)) {
    const plainCollection = data.map(item => camelize(item));
    const entityCollection = plainCollection.map(item => plainToClass(entityCtor, item));
    return entityCollection;
  }
  else {
    if (data) {
      const plainItem = camelize(data);
      const entity = plainToClass(entityCtor, plainItem);
      return entity;
    }
    return null;
  }
}

const camelize = (obj: Record<string, unknown>) => {
  const result = transform(obj, (result: Record<string, unknown>, value: unknown, key: string, target) => {
    const camelKey = isArray(target) ? key : camelCase(key);
    result[camelKey] = isObject(value) ? camelize(value as Record<string, unknown>) : value;
  });
  return result;
}

const snakize = (obj: any) => {
  const result = transform(obj, (result: any, value: unknown, key: string, target) => {
    const camelKey = isArray(target) ? key : snakeCase(key).replace(/_(\d)/, '$1');
    result[camelKey] = isObject(value) ? snakize(value) : value;
  });
  return result;
}

async function tryCall(promise: Promise<Response>) {
  try {
    const result = await promise;
    return Promise.resolve(result);
  }
  catch (error: any) {
    console.error(error[0]);
    const result = { data: null, meta: null } as Response;
    return Promise.resolve(result);
  }
}
